import { Fragment, useState } from "react";
import {
  Button,
  EmptyState,
  FormModal,
  Input,
  Page,
  ResponsiveGrid,
  Select,
  Text,
} from "@marekl/adminer/components";
import { pluralizeCzech } from "@marekl/adminer/format";
import { useConfirm } from "@marekl/adminer/hooks";
import { notifyError } from "@marekl/adminer/api";
import {
  useCreateFormationSize,
  useCreateFormationTable,
  useDeleteFormationSize,
  useDeleteFormationTable,
  useUpdateFormationSize,
  useUpdateFormationTable,
  type AgeAggregation,
  type FormationSize,
  type FormationTable,
  useDuplicateFormationTable,
} from "../../../lib/queries/rules";
import { AGE_AGGREGATION_LABELS, formatCountRange } from "../../../lib/rulesLabels";
import { checkFormationSizes } from "../../../lib/rulesChecks";
import { RuleChecks } from "../../../components/RuleChecks";
import { ComponentCard } from "./ComponentCard";
import {
  isOptionalInteger,
  isPositiveInteger,
  numberField,
  optionalNumber,
} from "./formValues";
import { reorderRows } from "./reorder";
import tableStyles from "./componentTables.module.css";
import type { ID } from "../../../lib/id";

type ModalState =
  | { kind: "closed" }
  | { kind: "table"; table?: FormationTable }
  | { kind: "row"; tableId: ID; row?: FormationSize };

interface RowForm {
  code: string;
  name: string;
  min_count: string;
  max_count: string;
  age_aggregation: AgeAggregation;
}

const emptyRowForm: RowForm = {
  code: "",
  name: "",
  min_count: "1",
  max_count: "",
  age_aggregation: "own",
};

export function FormationTablesTab({
  ruleSetId,
  readOnly,
  lockedTableIds,
  tables,
}: {
  ruleSetId: ID;
  readOnly: boolean;
  /**
   * Ids of the tables whose rungs may no longer be deleted: the set is in
   * service AND a discipline builds its matrix on this table.
   *
   * Per table, not per tab. A ladder nobody references holds no
   * combinations, so deleting a rung of it drops nothing and the server
   * allows it — the same reason the table's own „Smazat" stays. Hiding the
   * button on every table would have contradicted that, and left an admin
   * able to delete a whole unused ladder but not one row of it.
   */
  lockedTableIds: Set<ID>;
  tables: FormationTable[];
}) {
  const confirm = useConfirm();
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [tableName, setTableName] = useState("");
  const [rowForm, setRowForm] = useState<RowForm>(emptyRowForm);

  const createTable = useCreateFormationTable(ruleSetId);
  const updateTable = useUpdateFormationTable();
  const deleteTable = useDeleteFormationTable();
  const duplicateTable = useDuplicateFormationTable();
  const createRow = useCreateFormationSize();
  const updateRow = useUpdateFormationSize();
  const deleteRow = useDeleteFormationSize();

  const close = () => {
    setModal({ kind: "closed" });
    createTable.reset();
    updateTable.reset();
    duplicateTable.reset();
    createRow.reset();
    updateRow.reset();
  };

  const openTable = (table?: FormationTable) => {
    setTableName(table?.name ?? "");
    createTable.reset();
    updateTable.reset();
    setModal({ kind: "table", table });
  };

  const openRow = (tableId: ID, row?: FormationSize) => {
    setRowForm(
      row
        ? {
            code: row.code,
            name: row.name,
            min_count: String(row.min_count),
            max_count: numberField(row.max_count),
            age_aggregation: row.age_aggregation,
          }
        : emptyRowForm,
    );
    createRow.reset();
    updateRow.reset();
    setModal({ kind: "row", tableId, row });
  };

  const submitTable = () => {
    if (modal.kind !== "table") return;
    const payload = { name: tableName.trim() };
    if (modal.table) {
      updateTable.mutate({ id: modal.table.id, payload }, { onSuccess: close });
    } else {
      createTable.mutate(payload, { onSuccess: close });
    }
  };

  const submitRow = () => {
    if (modal.kind !== "row") return;
    const payload = {
      code: rowForm.code.trim(),
      name: rowForm.name.trim(),
      min_count: Number(rowForm.min_count),
      max_count: optionalNumber(rowForm.max_count),
      age_aggregation: rowForm.age_aggregation,
      sort_order:
        modal.row?.sort_order ??
        (tables.find((t) => t.id === modal.tableId)?.sizes ?? []).length,
    };
    if (modal.row) {
      updateRow.mutate({ id: modal.row.id, payload }, { onSuccess: close });
    } else {
      createRow.mutate({ tableId: modal.tableId, payload }, { onSuccess: close });
    }
  };

  const move = (table: FormationTable, index: number, direction: -1 | 1) => {
    for (const { row, sort_order } of reorderRows(table.sizes ?? [], index, direction)) {
      updateRow.mutate({
        id: row.id,
        payload: {
          code: row.code,
          name: row.name,
          min_count: row.min_count,
          max_count: row.max_count ?? null,
          age_aggregation: row.age_aggregation,
          sort_order,
        },
      }, { onError: (err) => notifyError(err) });
    }
  };

  const rowInvalid =
    !rowForm.code.trim() ||
    !rowForm.name.trim() ||
    !isPositiveInteger(rowForm.min_count) ||
    !isOptionalInteger(rowForm.max_count);

  return (
    <>
      <Page.SectionHeader
        title="Tabulky formací"
        meta={
          readOnly ? undefined : (
            <Button variant="primary" onClick={() => openTable()}>
              Nová tabulka
            </Button>
          )
        }
      />
      <Text variant="muted">
        Podle velikosti formace se určuje i to, čí věk rozhoduje: sólistka svým
        vlastním, duo/trio věkem nejstaršího, skupiny průměrem.
      </Text>

      {tables.length === 0 ? (
        <EmptyState
          title="Žádné tabulky formací"
          description="Založte tabulku a přidejte do ní velikosti formací (Sólo, Duo/Trio, …)."
        />
      ) : (
        tables.map((table) => (
          <Fragment key={table.id}>
            <RuleChecks warnings={checkFormationSizes(table.sizes ?? [])} />
          <ComponentCard<FormationSize>
            title={table.name}
            subtitle={<Text variant="muted">{(table.sizes ?? []).length} {pluralizeCzech((table.sizes ?? []).length, ["formace", "formace", "formací"])}</Text>}
            readOnly={readOnly}
          lockDelete={lockedTableIds.has(table.id)}
            rows={table.sizes ?? []}
            emptyTitle="Zatím bez formací"
            emptyDescription="Přidejte první velikost formace."
            addLabel="Přidat formaci"
            columns={[
              {
                key: "code",
                header: "Kód",
                tdClass: tableStyles.code,
                render: (r) => <Text variant="mono">{r.code}</Text>,
              },
              { key: "name", header: "Název", tdClass: tableStyles.name, render: (r) => r.name },
              {
                key: "count",
                header: "Počet",
                tdClass: tableStyles.value,
                render: (r) => formatCountRange(r.min_count, r.max_count ?? null),
              },
              {
                key: "aggregation",
                header: "Věk formace",
                tdClass: tableStyles.value,
                render: (r) => AGE_AGGREGATION_LABELS[r.age_aggregation],
              },
            ]}
            onEditTable={() => openTable(table)}
            onDuplicateTable={() => duplicateTable.mutate({ id: table.id }, { onError: (err) => notifyError(err) })}
            onDeleteTable={() =>
              confirm({
                title: "Smazat tabulku formací",
                description: (
                  <>
                    Smaže se tabulka <strong>{table.name}</strong> i všechny její formace.
                    Tabulku, kterou používá některá disciplína, smazat nelze.
                  </>
                ),
                confirmLabel: "Smazat",
                variant: "danger",
                onConfirm: () => deleteTable.mutateAsync(table.id),
              })
            }
            onAddRow={() => openRow(table.id)}
            onEditRow={(row) => openRow(table.id, row)}
            onDeleteRow={(row) =>
              confirm({
                title: "Smazat formaci",
                description: (
                  <>
                    Smaže se formace <strong>{row.name}</strong>. Ve všech disciplínách, které
                    tuto tabulku používají, zaniknou i kombinace v tomto sloupci, včetně jejich
                    vlastních nastavení.
                  </>
                ),
                confirmLabel: "Smazat",
                variant: "danger",
                onConfirm: () => deleteRow.mutateAsync(row.id),
              })
            }
            onMoveRow={(index, direction) => move(table, index, direction)}
          />
          </Fragment>
        ))
      )}

      <FormModal
        open={modal.kind === "table"}
        title={modal.kind === "table" && modal.table ? "Upravit tabulku" : "Nová tabulka formací"}
        onClose={close}
        submitLabel="Uložit"
        busy={createTable.isPending || updateTable.isPending}
        canSubmit={!!tableName.trim()}
        error={createTable.error || updateTable.error}
        onSubmit={submitTable}
      >
        <Input
          label="Název tabulky"
          required
          placeholder="Standardní formace"
          value={tableName}
          onChange={(e) => setTableName(e.target.value)}
        />
      </FormModal>

      <FormModal
        open={modal.kind === "row"}
        title={modal.kind === "row" && modal.row ? "Upravit formaci" : "Nová formace"}
        onClose={close}
        submitLabel="Uložit"
        busy={createRow.isPending || updateRow.isPending}
        canSubmit={!rowInvalid}
        error={createRow.error || updateRow.error}
        onSubmit={submitRow}
      >
        <ResponsiveGrid columns={2}>
          <Input
            label="Kód"
            required
            placeholder="SOLO"
            value={rowForm.code}
            onChange={(e) => setRowForm({ ...rowForm, code: e.target.value })}
          />
          <Input
            label="Název"
            required
            placeholder="Sólo"
            value={rowForm.name}
            onChange={(e) => setRowForm({ ...rowForm, name: e.target.value })}
          />
        </ResponsiveGrid>
        <ResponsiveGrid columns={2}>
          <Input
            label="Nejméně osob"
            required
            type="number"
            min={1}
            value={rowForm.min_count}
            onChange={(e) => setRowForm({ ...rowForm, min_count: e.target.value })}
          />
          <Input
            label="Nejvíce osob"
            type="number"
            min={1}
            value={rowForm.max_count}
            onChange={(e) => setRowForm({ ...rowForm, max_count: e.target.value })}
            hint="Prázdné = bez horní hranice (velká formace)."
          />
        </ResponsiveGrid>
        <Select
          label="Věk formace"
          value={rowForm.age_aggregation}
          onChange={(e) =>
            setRowForm({ ...rowForm, age_aggregation: e.target.value as AgeAggregation })
          }
          hint="Podle čeho se formace zařadí do věkové kategorie."
        >
          {Object.entries(AGE_AGGREGATION_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
      </FormModal>


    </>
  );
}
